/* eslint-disable react/display-name */
import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import TextField from '../../../../shared/TextField';
import Select from '../../../../shared/Select';
import { FormControl, Collapse, InputAdornment, Box, MenuItem, InputLabel } from '@mui/material';
import { Search } from '@mui/icons-material';
import Button from '../../../../shared/Button';
import SearchFilters, { FilterBadge } from '../../../../shared/SearchFilters';
import PaginationResults from '../../../../shared/Pagination/PaginationResults';
import AdvancedSearch from '../../../../shared/AdvancedSearch';
import { useLocales, useTheme } from '../../../../../hooks';
import {
  PaginationMeta,
  SmartQueryBodyPaginated,
  SmartQueryConditionBody,
  SmartQueryFieldSymbol,
  SmartQueryOperatorSymbol,
  SmartQuerySearchIndex,
  SmartQuerySortingSymbol,
  SmartQuerySortingDirection,
  SmartContentCollectionQuerySortingDirection,
  SavedSearchResponse
} from '../../../../../API';
import { makeStyles } from 'tss-react/mui';
import { LocaleKeys } from '../../../../../locales/i18n';
import ContentSearchButton from '../ContentSearchButton';
import ContentTypeSelect from '../../../../shared/FormControl/FormControlMultiSelec';
import { useData } from '../../../../../data-layer';
import { useRecoilValue } from 'recoil';
import SmartQueryFieldRenderer from '../../../shared/SmartQueryFieldRenderer';
import { SavedSearches } from '../SavedSearches/SavedSearches';
import { WithFilters } from '../../../../shared/SearchFilters';

interface IContentSearchSmartFiltersProps {
  mediaList: string[];
  isLoading?: boolean;
  paginationMeta: PaginationMeta | undefined;
  onSubmit: (evt?: React.FormEvent<HTMLElement>) => void;
  onExport: () => void;
  contentTypes?: string[];
  contentTypesAllowed?: string[];
}
const SortNone = 'SORT_NONE';

enum SortBy {
  SORT_PUBLISH_START = 'SORT_PUBLISH_START',
  SORT_TITLE_ALPHABETICAL = 'SORT_TITLE_ALPHABETICAL',
  SORT_RECENTLY_INGESTED_ASC = 'SORT_RECENTLY_INGESTED_ASC',
  SORT_RECENTLY_INGESTED_DESC = 'SORT_RECENTLY_INGESTED_DESC',
  SORT_DATE_CREATED_ASC = 'SORT_DATE_CREATED_ASC',
  SORT_DATE_MODIFIED_DESC = 'SORT_DATE_MODIFIED_DESC',
  SORT_YEAR_RELEASED_DESC = 'SORT_YEAR_RELEASED_DESC'
}

type SortValue = {
  field: SmartQuerySortingSymbol;
  direction: SmartContentCollectionQuerySortingDirection;
  localizationKey: LocaleKeys;
};

const SortOptions: Record<SortBy, SortValue> = {
  [SortBy.SORT_PUBLISH_START]: {
    field: SmartQuerySortingSymbol.PUBLISH_WINDOW_START_DATE,
    direction: SmartContentCollectionQuerySortingDirection.ASC,
    localizationKey: 'general.sort.publish_window_start'
  },
  [SortBy.SORT_TITLE_ALPHABETICAL]: {
    field: SmartQuerySortingSymbol.TITLE,
    direction: SmartContentCollectionQuerySortingDirection.ASC,
    localizationKey: 'general.sort.title'
  },
  [SortBy.SORT_RECENTLY_INGESTED_ASC]: {
    field: SmartQuerySortingSymbol.INGEST_DATE,
    direction: SmartContentCollectionQuerySortingDirection.ASC,
    localizationKey: 'general.sort.recently_ingested_date_asc'
  },
  [SortBy.SORT_RECENTLY_INGESTED_DESC]: {
    field: SmartQuerySortingSymbol.INGEST_DATE,
    direction: SmartContentCollectionQuerySortingDirection.DESC,
    localizationKey: 'general.sort.recently_ingested_date_desc'
  },
  [SortBy.SORT_DATE_CREATED_ASC]: {
    field: SmartQuerySortingSymbol.DATE_CREATED,
    direction: SmartContentCollectionQuerySortingDirection.ASC,
    localizationKey: 'general.sort.date_created'
  },
  [SortBy.SORT_DATE_MODIFIED_DESC]: {
    field: SmartQuerySortingSymbol.DATE_MODIFIED,
    direction: SmartContentCollectionQuerySortingDirection.DESC,
    localizationKey: 'general.sort.date_modified'
  },
  [SortBy.SORT_YEAR_RELEASED_DESC]: {
    field: SmartQuerySortingSymbol.YEAR_RELEASED,
    direction: SmartContentCollectionQuerySortingDirection.DESC,
    localizationKey: 'general.sort.year_released'
  }
};

// Map SmartQueryFieldSymbol to form field names
const symbolToFormFieldMap: Partial<Record<SmartQueryFieldSymbol, keyof SmartQueryFormParams>> = {
  [SmartQueryFieldSymbol.TITLE]: 'title',
  [SmartQueryFieldSymbol.DESCRIPTION]: 'description',
  [SmartQueryFieldSymbol.KEYWORDS]: 'keywords',
  [SmartQueryFieldSymbol.CONTRIBUTORS_NAME]: 'contributorsName',
  [SmartQueryFieldSymbol.ACTOR]: 'actor',
  [SmartQueryFieldSymbol.DIRECTOR]: 'director',
  [SmartQueryFieldSymbol.GENRE]: 'genre',
  [SmartQueryFieldSymbol.CONTENT_TYPE]: 'contentType',
  [SmartQueryFieldSymbol.CONTENT_GROUPS]: 'contentGroups',
  [SmartQueryFieldSymbol.CONTENT_VERTICAL]: 'contentVertical',
  [SmartQueryFieldSymbol.RATING]: 'rating',
  [SmartQueryFieldSymbol.SUPPLIER]: 'supplier',
  [SmartQueryFieldSymbol.YEAR_RELEASED]: 'yearReleased',
  [SmartQueryFieldSymbol.DATE_CREATED]: 'dateCreated',
  [SmartQueryFieldSymbol.DATE_MODIFIED]: 'dateModified',
  [SmartQueryFieldSymbol.PUBLISH_WINDOW_START_DATE]: 'publishWindowStartDate',
  [SmartQueryFieldSymbol.PUBLISH_WINDOW_END_DATE]: 'publishWindowEndDate',
  [SmartQueryFieldSymbol.ID]: 'id',
  [SmartQueryFieldSymbol.ALLOWED_COUNTRY]: 'allowedCountry',
  [SmartQueryFieldSymbol.PUBLISH_WINDOW_COUNTRY]: 'publishWindowCountry',
  [SmartQueryFieldSymbol.IS_PREMIUM]: 'isPremium',
  [SmartQueryFieldSymbol.DOWNLOADABLE]: 'downloadable',
  [SmartQueryFieldSymbol.ORPHANED]: 'orphaned'
};

const excludedFields = [SmartQueryFieldSymbol.CONTENT_TYPE];

enum WildcardSearchField {
  TITLE = 'title',
  DESCRIPTION = 'description',
  KEYWORDS = 'keywords',
  ID = 'id'
}

// Special form fields that require custom handling
enum SpecialFormField {
  CONTENT_TYPE = 'contentType',
  SEARCH_TERM = 'searchTerm',
  TERM = 'term'
}

export interface SmartQueryFormParams {
  // Text search fields
  searchTerm?: string;
  title?: string;
  description?: string;
  keywords?: string;
  term?: string;

  // People fields
  contributorsName?: string;
  actor?: string;
  director?: string;

  // Categorization fields
  genre?: string;
  contentType?: string[]; // Array for any-of operator
  contentGroups?: string;
  contentVertical?: string;
  rating?: string;

  // Supplier/Source fields
  supplier?: string;

  // Sports-related fields
  teamId?: string;
  sportId?: string;
  leagueId?: string;
  matchId?: string;

  // Date fields
  startDate?: string;
  endDate?: string;
  yearReleased?: number;
  dateCreated?: string | string[];
  dateModified?: string | string[];
  publishWindowStartDate?: string | string[];
  publishWindowEndDate?: string | string[];

  // Date field operators
  dateCreatedOperator?: SmartQueryOperatorSymbol;
  dateModifiedOperator?: SmartQueryOperatorSymbol;
  publishWindowStartDateOperator?: SmartQueryOperatorSymbol;
  publishWindowEndDateOperator?: SmartQueryOperatorSymbol;

  // Other fields
  id?: string;
  allowedCountry?: string;
  publishWindowCountry?: string;
  transmissionStatus?: string;
  isPremium?: boolean;
  downloadable?: boolean;
  orphaned?: boolean;

  // Pagination
  limit?: number;
  page?: number;

  // Sorting - using string key to match SortBy enum
  sortBy?: string;
}

export type ContentSearchSmartFiltersData = WithFilters<SmartQueryFormParams, SmartQueryFieldSymbol>;

const useStyles = makeStyles()((theme) => ({
  topSection: {
    padding: theme.spacing(4, 4, 3),
    display: 'flex',
    flexDirection: 'column'
  },
  topSectionSearch: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3)
  },
  topSectionAdvanced: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(2)
  },
  topSectionAdvancedRight: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3)
  },
  // eslint-disable-next-line tss-unused-classes/unused-classes
  contentTypeFilter: {
    minWidth: 135,
    marginBottom: theme.spacing(4)
  },
  advancedSearchContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  sortBySelector: {
    minWidth: 180,
    marginBottom: theme.spacing(4)
  },
  searchField: {
    minWidth: 200
  }
}));

function ContentSearchSmartFilters({
  onSubmit,
  onExport,
  mediaList,
  isLoading,
  paginationMeta,
  contentTypes = [],
  contentTypesAllowed = []
}: IContentSearchSmartFiltersProps): JSX.Element {
  const { classes } = useStyles();
  const { t, localize } = useLocales();
  const { formControlColor } = useTheme();

  const {
    collections: {
      state: { withCollectionQueryFields }
    }
  } = useData();

  const queryFields = useRecoilValue(withCollectionQueryFields);
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const { control, setValue, getValues, watch, reset } = useFormContext<ContentSearchSmartFiltersData>();

  const filters = watch('_filters');

  useEffect(() => {
    if (filters && (filters?.length || 0) > 0) {
      const validSmartQueryFields = filters.filter((filter: SmartQueryFieldSymbol) => symbolToFormFieldMap[filter]);
      // If the number of valid smart query fields is not the same as the number of filters
      // reset the form to prevent old filters from being applied
      if (validSmartQueryFields.length !== filters.length) {
        reset({});
        setValue('_filters', []);
      }
      setIsAdvancedSearchOpen(validSmartQueryFields.length > 0);
    }
  }, [filters]);

  // Filter queryFields to only include those that have a form field mapping
  const availableFields = queryFields.filter(
    (field) => symbolToFormFieldMap[field.symbol] && !excludedFields.includes(field.symbol)
  );

  // Helper function to get SortBy key from field and direction
  const getSortValueFromObject = (value: string): SortBy | typeof SortNone => {
    for (const key of Object.values(SortBy)) {
      if (value === key) {
        return key;
      }
    }
    return SortNone;
  };

  // Sort filters alphabetically by their localized labels
  const sortedFilterSymbols = [...availableFields].sort((a, b) => {
    const labelA = localize(a.name);
    const labelB = localize(b.name);
    return labelA.localeCompare(labelB);
  });

  // Create sorted actions object with localized labels
  const searchFilterActions = sortedFilterSymbols.reduce(
    (actions, field) => ({
      ...actions,
      [field.symbol]: localize(field.name)
    }),
    {}
  ) as Record<SmartQueryFieldSymbol, string>;

  const handleRemoveSearchFilter = (symbol: SmartQueryFieldSymbol) => {
    const formField = symbolToFormFieldMap[symbol];
    if (formField) {
      setValue(formField, undefined);
    }
  };

  const onLoadSavedSearch = (savedSearch: SavedSearchResponse) => {
    const savedSearchFilters: ContentSearchSmartFiltersData = {
      ...(savedSearch.query as ContentSearchSmartFiltersData),
      // Page and Limit should not be loaded from saved search
      // Reset back to page 1 and the current limit
      page: 1,
      limit: getValues('limit')
    };
    reset(savedSearchFilters);
    onSubmit();
  };

  return (
    <form className={classes.topSection} onSubmit={onSubmit}>
      <div className={classes.topSectionSearch}>
        <Controller
          name="searchTerm"
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextField
              fullWidth
              label={t('collections.search_wildcard')}
              value={value || ''}
              clearable={true}
              onClear={() => {
                setValue('searchTerm', '');
                onSubmit();
              }}
              onChange={onChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
            />
          )}
        />

        {/* Content Type Selector */}
        <Controller
          name="contentType"
          control={control}
          render={({ field: { value } }) => (
            <ContentTypeSelect
              formControlColor={formControlColor}
              classes={classes}
              value={value || []}
              setValue={setValue as unknown as (name: string, value: string[]) => void}
              contentTypes={contentTypes}
              contentTypesAllowed={contentTypesAllowed}
              t={t}
              contentSearchFiltersTestIds="contentType"
            />
          )}
        />

        {/* Sort By Selector */}
        <Controller
          name="sortBy"
          control={control}
          render={({ field: { value } }) => (
            <FormControl color={formControlColor} className={classes.sortBySelector}>
              <InputLabel id="contentSortBy">{t('pagination.sort_by')}</InputLabel>
              <Select
                label={t('pagination.sort_by')}
                labelId="contentSortBy"
                value={value ? getSortValueFromObject(value) : SortNone}
                onChange={({ target: { value: sortValue } }) => {
                  if (sortValue === SortNone) {
                    setValue('sortBy', undefined);
                  } else if (SortOptions[sortValue as SortBy]) {
                    setValue('sortBy', sortValue);
                  }
                }}
              >
                <MenuItem value={SortNone}>{t('general.sort.none')}</MenuItem>
                {Object.keys(SortOptions).map((option) => (
                  <MenuItem key={option} value={option}>
                    {t(SortOptions[option as SortBy].localizationKey)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />

        <ContentSearchButton handleDropdownClick={onExport} loading={isLoading}>
          <Button type="submit" size="small">
            {t('general.search')}
          </Button>
        </ContentSearchButton>
      </div>

      {/* Advanced Search Section */}
      <Collapse in={isAdvancedSearchOpen}>
        <div className={classes.advancedSearchContainer}>
          <SearchFilters
            actions={searchFilterActions}
            onRemove={handleRemoveSearchFilter}
            render={(FilterWrap) => {
              // Dynamic field renderer based on queryFields
              const renderField = (queryField: (typeof availableFields)[0]) => {
                const formFieldName = symbolToFormFieldMap[queryField.symbol];
                if (!formFieldName) return null;

                const operatorFieldName = `${formFieldName}Operator` as keyof SmartQueryFormParams;
                const isDateField = queryField.type === 'DATETIME';

                // Initialize operator for date fields if not set
                if (isDateField && !watch(operatorFieldName)) {
                  setValue(operatorFieldName, SmartQueryOperatorSymbol.EQ as never);
                }

                return (
                  <FilterWrap key={queryField.symbol} name={queryField.symbol}>
                    <Controller
                      name={formFieldName}
                      control={control}
                      render={({ field: { value } }) => {
                        const currentOperator =
                          isDateField && watch(operatorFieldName)
                            ? (watch(operatorFieldName) as SmartQueryOperatorSymbol)
                            : SmartQueryOperatorSymbol.CONTAINS;

                        return (
                          <SmartQueryFieldRenderer
                            field={queryField}
                            value={value as string | number | string[] | number[] | boolean | undefined}
                            onChange={(newValue) => setValue(formFieldName, newValue as never)}
                            operator={currentOperator}
                            onOperatorChange={
                              isDateField ? (newOp) => setValue(operatorFieldName, newOp as never) : undefined
                            }
                            className={classes.searchField}
                          />
                        );
                      }}
                    />
                  </FilterWrap>
                );
              };

              return <>{sortedFilterSymbols.map((field) => renderField(field))}</>;
            }}
          />
        </div>
      </Collapse>

      <div className={classes.topSectionAdvanced}>
        <PaginationResults list={mediaList} meta={paginationMeta} isLoading={isLoading} />
        <Box className={classes.topSectionAdvancedRight}>
          <FilterBadge filters={getValues('_filters')} />
          <AdvancedSearch open={isAdvancedSearchOpen} onChange={setIsAdvancedSearchOpen} />
          <SavedSearches onLoad={onLoadSavedSearch} />
        </Box>
      </div>
    </form>
  );
}

export default ContentSearchSmartFilters;

// Create reverse mapping from form field to symbol
const formFieldToSymbolMap: Partial<Record<keyof SmartQueryFormParams, SmartQueryFieldSymbol>> = Object.entries(
  symbolToFormFieldMap
).reduce(
  (acc, [symbol, formField]) => ({
    ...acc,
    [formField]: symbol as SmartQueryFieldSymbol
  }),
  {}
);

// Helper function to convert form data to SmartQueryBodyPaginated
export function formDataToSmartQuery(
  data: ContentSearchSmartFiltersData,
  limit = 100,
  page = 1
): SmartQueryBodyPaginated {
  const conditions: SmartQueryConditionBody[] = [];
  let fieldConditions: SmartQueryConditionBody[] = [];

  // Normalize contentType to always be an array (since it's defined as string[] in SmartQueryFormParams)
  const contentTypeArray = data.contentType
    ? Array.isArray(data.contentType)
      ? data.contentType
      : [data.contentType as string]
    : [];

  // Handle contentType with any-of operator
  if (contentTypeArray.length > 0) {
    fieldConditions.push({
      field: SmartQueryFieldSymbol.CONTENT_TYPE,
      operator: SmartQueryOperatorSymbol.ANY_OF,
      value: contentTypeArray,
      key: `0.${fieldConditions.length}`
    });
  }

  // Handle searchTerm - expand to title, description, keywords, and id with CONTAINS operator in an OR group
  if (data.searchTerm && data.searchTerm.trim()) {
    const searchValue = data.searchTerm.trim();

    // Add OR group operator first
    fieldConditions = fieldConditions.concat({
      operator: SmartQueryOperatorSymbol.OR,
      key: `0.${fieldConditions.length}`
    });

    // Add the search conditions within the OR group using enum
    const wildcardFields = [
      { field: SmartQueryFieldSymbol.TITLE, enumValue: WildcardSearchField.TITLE },
      { field: SmartQueryFieldSymbol.DESCRIPTION, enumValue: WildcardSearchField.DESCRIPTION },
      { field: SmartQueryFieldSymbol.KEYWORDS, enumValue: WildcardSearchField.KEYWORDS },
      { field: SmartQueryFieldSymbol.ID, enumValue: WildcardSearchField.ID }
    ];

    fieldConditions = fieldConditions.concat(
      wildcardFields.map((item, index) => ({
        field: item.field,
        operator: SmartQueryOperatorSymbol.CONTAINS,
        value: searchValue,
        key: `0.${fieldConditions.length - 1}.${index}`
      }))
    );
  }

  // Collect all field conditions (with contains operator for strings, eq for others)
  for (const [formField, fieldSymbol] of Object.entries(formFieldToSymbolMap)) {
    // Skip contentType as it's already handled above
    if (formField === SpecialFormField.CONTENT_TYPE) continue;
    // Skip searchTerm as it's already handled above
    if (formField === SpecialFormField.SEARCH_TERM) continue;
    // Skip keywords and term if searchTerm is being used (to avoid duplication)
    if (data.searchTerm?.trim() && (formField === WildcardSearchField.KEYWORDS || formField === SpecialFormField.TERM))
      continue;

    // Only include fields that are in the _filters array (i.e., actively selected filters)
    // If _filters is not provided, include all fields (for backward compatibility with tests)
    if (data._filters && !data._filters.includes(fieldSymbol as SmartQueryFieldSymbol)) continue;

    const value = data[formField as keyof SmartQueryFormParams];

    // Skip empty values
    if (value === undefined || value === null || value === '') continue;

    // Check if this field has a custom operator (for date fields)
    const operatorFieldName = `${formField}Operator` as keyof SmartQueryFormParams;
    const customOperator = data[operatorFieldName] as SmartQueryOperatorSymbol | undefined;

    // Normalize arrays: if value is a string but should be an array, convert it
    // This handles cases where form data has inconsistent types
    if (Array.isArray(value)) {
      // If it's already an array and not empty, use the custom operator or ANY_OF
      if (value.length === 0) continue;

      fieldConditions.push({
        field: fieldSymbol as SmartQueryFieldSymbol,
        operator: customOperator || SmartQueryOperatorSymbol.ANY_OF,
        value,
        key: `0.${fieldConditions.length}`
      });
    } else if (typeof value === 'string' && value.trim()) {
      // String fields use custom operator or contains operator
      fieldConditions.push({
        field: fieldSymbol as SmartQueryFieldSymbol,
        operator: customOperator || SmartQueryOperatorSymbol.CONTAINS,
        value: value.trim(),
        key: `0.${fieldConditions.length}`
      });
    } else if (typeof value === 'number') {
      // Number fields use custom operator or eq operator
      fieldConditions.push({
        field: fieldSymbol as SmartQueryFieldSymbol,
        operator: customOperator || SmartQueryOperatorSymbol.EQ,
        value,
        key: `0.${fieldConditions.length}`
      });
    } else if (typeof value === 'boolean') {
      // Boolean fields use eq operator (convert to string for API)
      fieldConditions.push({
        field: fieldSymbol as SmartQueryFieldSymbol,
        operator: SmartQueryOperatorSymbol.EQ,
        value: value.toString(),
        key: `0.${fieldConditions.length}`
      });
    }
  }

  // If we have conditions, add the AND group operator first, then the conditions
  if (fieldConditions.length > 0) {
    conditions.push({
      operator: SmartQueryOperatorSymbol.AND,
      key: '0'
    });
    conditions.push(...fieldConditions);
  }

  // Handle sorting - data.sortBy is now a SortBy enum key
  const sortings = [];
  if (data.sortBy && SortOptions[data.sortBy as SortBy]) {
    const sortOption = SortOptions[data.sortBy as SortBy];
    sortings.push({
      field: sortOption.field,
      direction: sortOption.direction as unknown as SmartQuerySortingDirection
    });
  }

  return {
    conditions,
    sortings,
    searchIndex: SmartQuerySearchIndex.CONTENT,
    limit: data.limit || limit,
    page: data.page || page
  };
}
